import { Component } from 'react';
import { firstValueFrom, Subscription } from 'rxjs';

import { lazyInject } from '../../../context/module';
import { PaymentService } from '../../../services/payments.service';
import { ScriptLoadService } from '../../../services/script-load.service';
import { SettingsService } from '../../../services/settings.service';
import { StringUtils } from '../../../utils/string-utils';
import { newAttemptId } from './checkout-format';
import styles from './paypal.component.module.scss';

/** Unit tests inject stubs here; production never passes it (`@lazyInject` wires the real services). */
export interface PayPalTestServices {
    paymentService?: Pick<PaymentService, 'createPayPalSubscription'>;
    settingsService?: Pick<SettingsService, 'featureFlags'>;
}

interface PayPalComponentProp {
    children?: any;

    /** PayPal plan id (`P-...`) to create a subscription for. */
    planId: string;

    /**
     * W0-12 (FIX-PLAN 2026-10-18): the gateway-agnostic plan name (`backer_monthly`, `patron_yearly`, …) the server
     * resolves to `planId`. Required for the server-side create; when absent the component can only use today's
     * client-side `actions.subscription.create`.
     */
    planName?: string;

    /**
     * W0-12: called when the server refuses to create the subscription (409 from the purchase policy — already
     * subscribed, change required, cross-gateway, payment update required, lease held). The error message is the
     * server's §0 text ("… Nothing was charged." is true: no PayPal object exists). PayPal's popup shows its own
     * generic failure; the parent shows ours.
     */
    onCreateFailed?: (err: Error) => void;

    __testServices?: PayPalTestServices;

    /**
     * Logged-in user's MongoDB ObjectId (hex string), passed as `custom_id`
     * on the subscription create call so the backend webhook handler can
     * resolve the user when the event arrives before /pay-paypal persists
     * the subscriptionId. See paypal-sdk-upgrade.md §4.2 + §4.3.1 step 4c.
     */
    userId?: string;

    /** Called with the PayPal subscription id (`I-...`) after approval. */
    completed: (subscriptionId?: string) => void;
}

interface PayPalComponentState {
    status: 'loading' | 'ready' | 'error';
    errorMessage?: string;
}

/**
 * Renders a PayPal subscription button and a "Debit or Credit Card" button
 * for a single plan.
 *
 * Uses `@paypal/paypal-js` (via `ScriptLoadService.loadPayPal()`) to load the
 * SDK and get back the `paypal` namespace directly, instead of polling
 * `window.paypal`. That's PayPal's officially-supported loader — it handles
 * script-tag bootstrap sequencing and client-id validation internally, and
 * is resilient to class of browser-extension interference that was breaking
 * our hand-rolled loader on QA.
 *
 * Three-state UI:
 *  - `loading`  spinner + copy while SDK downloads.
 *  - `ready`    SDK rendered both buttons; status hidden.
 *  - `error`    red alert + "Try again" button on timeout / render throw.
 *
 * Lifecycle:
 *  - `paypal.Buttons(...).render(#id)` binds to the container DOM once at
 *    first mount. Parent keeps the component mounted across billing-cycle
 *    switches (F3 per docs/proposed/paypal-sdk-upgrade.md).
 */
export class PayPalComponent extends Component<PayPalComponentProp, PayPalComponentState> {
    private readonly paypalButtonElementId = StringUtils.getRandomString('pp_');
    private readonly cardButtonElementId = StringUtils.getRandomString('cc_');

    @lazyInject(PaymentService) private _paymentService!: PaymentService;
    @lazyInject(SettingsService) private _settingsService!: SettingsService;

    /**
     * `settings.featureFlags.checkoutStatusGate` (W0-12). Read at click time, so a flip while the page is open
     * takes effect on the next click; `false` until settings arrive = today's behaviour.
     */
    private checkoutStatusGate = false;
    private flagsSub?: Subscription;

    readonly state: Readonly<PayPalComponentState> = { status: 'loading' };

    private get paymentService() {
        return this.props.__testServices?.paymentService ?? this._paymentService;
    }
    private get settingsService() {
        return this.props.__testServices?.settingsService ?? this._settingsService;
    }

    componentDidMount() {
        this.flagsSub = this.settingsService?.featureFlags?.subscribe({
            next: (ff) => { this.checkoutStatusGate = ff?.checkoutStatusGate === true; },
        });
        this.loadAndRender();
    }

    componentWillUnmount() {
        this.flagsSub?.unsubscribe();
    }

    /**
     * W0-12 (1): `POST /payments/paypal/create-subscription {planName, attemptId}` → `{subscriptionId}`. The JS SDK
     * accepts a promise of an id from `createSubscription`, so the server decides (policy + lease) and creates the
     * subscription with `PayPal-Request-Id = attemptId` before PayPal ever shows the approval popup.
     */
    private async createSubscriptionServerSide(planName: string): Promise<string> {
        try {
            const res = await firstValueFrom(this.paymentService.createPayPalSubscription(planName, newAttemptId()));
            if (!res?.subscriptionId) {
                throw new Error('The server did not return a PayPal subscription id. Nothing was charged.');
            }
            return res.subscriptionId;
        } catch (err: any) {
            // eslint-disable-next-line no-console
            console.error('[PayPal] server-side createSubscription failed', { planName, errMessage: err?.message });
            this.props.onCreateFailed?.(err instanceof Error ? err : new Error(String(err?.message ?? err)));
            throw err;
        }
    }

    private async loadAndRender() {
        this.setState({ status: 'loading', errorMessage: undefined });
        try {
            const paypal = await ScriptLoadService.loadPayPal();
            if (!paypal) {
                // SSR / no-window codepath — shouldn't happen on componentDidMount
                // but guard regardless so a future misuse degrades cleanly.
                throw new Error('PayPal SDK unavailable (no window).');
            }
            this.renderButtons(paypal);
            this.setState({ status: 'ready' });
        } catch (e: any) {
            // eslint-disable-next-line no-console
            console.error('[PayPal] loadAndRender failed', e);
            this.setState({
                status: 'error',
                errorMessage:
                    e?.message
                    || 'Payment provider could not load. Check your connection, disable ad/privacy blockers, and try again.',
            });
        }
    }

    private renderButtons(paypal: any) {
        const base = {
            createSubscription: (_data: unknown, actions: any) => {
                // W0-12: flag on + plan name known → the server creates the subscription; otherwise today's path.
                if (this.checkoutStatusGate && this.props.planName) {
                    return this.createSubscriptionServerSide(this.props.planName);
                }
                const req: { plan_id: string; custom_id?: string } = { plan_id: this.props.planId };
                if (this.props.userId) req.custom_id = this.props.userId;
                return actions.subscription.create(req).catch((err: any) => {
                    // Capture the full error body for support — Chrome devtools
                    // truncates long error strings in the default logger render.
                    // eslint-disable-next-line no-console
                    console.error('[PayPal] createSubscription failed', {
                        planId: this.props.planId,
                        custom_id: req.custom_id || null,
                        errMessage: err?.message,
                        errStack: err?.stack,
                        err,
                    });
                    throw err;
                });
            },
            onApprove: (data: { subscriptionID: string }) => {
                this.props.completed(data.subscriptionID);
            },
        };

        paypal.Buttons({
            ...base,
            style: {
                shape: 'rect',
                color: 'blue',
                layout: 'vertical',
                label: 'subscribe',
                height: 40,
            },
            fundingSource: paypal.FUNDING.PAYPAL,
        }).render(`#${this.paypalButtonElementId}`);

        const card = paypal.Buttons({
            ...base,
            style: {
                shape: 'rect',
                color: 'black',
                layout: 'vertical',
                label: 'pay',
                height: 40,
            },
            fundingSource: paypal.FUNDING.CARD,
        });
        if (card.isEligible()) {
            card.render(`#${this.cardButtonElementId}`);
        }
    }

    render() {
        const { status, errorMessage } = this.state;
        return (
            <div className={styles['paypal-buttons']}>
                {status === 'loading' && (
                    <div className={styles['paypal-status']} role="status" aria-live="polite">
                        <span className={`spinner-border spinner-border-sm ${styles['paypal-spinner']}`} aria-hidden="true"></span>
                        <span>Loading secure checkout…</span>
                    </div>
                )}
                {status === 'error' && (
                    <div className={styles['paypal-error']} role="alert">
                        <div>{errorMessage || 'Could not load PayPal.'}</div>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary mt-2"
                            onClick={() => this.loadAndRender()}>
                            Try again
                        </button>
                    </div>
                )}
                {/*
                  Button containers stay mounted in every state. PayPal's
                  `.render('#id')` binds these DOM nodes on first render;
                  unmount = orphan iframes + lost popup handshake.
                */}
                <div
                    id={this.paypalButtonElementId}
                    style={{ display: status === 'ready' ? 'block' : 'none' }}
                />
                <div
                    id={this.cardButtonElementId}
                    style={{ display: status === 'ready' ? 'block' : 'none' }}
                />
            </div>
        );
    }
}
